import { Badge } from "@fluentui/react-components";
import { format, startOfDay, subMinutes } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import baggageIcon from "../../assests/notifications/baggageAlert.svg";
import paxIcon from "../../assests/notifications/paxAlert.svg";
import weatherIcon from "../../assests/notifications/weatherAlert.svg";
import { CATEGORY_OPTIONS } from "../../components-v2/notifications/constants";
import {
  DismissNotificationsPayload,
  NotificationFilterState,
  NotificationItem,
  NotificationStatusTab,
  TimeFilter,
} from "../../components-v2/notifications/types";
import { toSentenceCase } from "../common/CommonHelperFunctions";
import {
  convertToUTC,
  formatRelativeTime,
} from "../common/DateHelperFunctions";
import { processMessageTitle } from "./NotificationHelperFunctions";
type BadgeColor = React.ComponentProps<typeof Badge>["color"];

const DISMISS_PAYLOAD_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";
const OLDEST_NOTIFICATION_DATE = "1970-01-01T00:00:00";

export const getTimeFilterWindowInMinutes = (
  timeFilter: TimeFilter
): number | null => {
  switch (timeFilter) {
    case "30min":
      return 30;
    case "1Hour":
      return 60;
    case "6Hours":
      return 6 * 60;
    case "12Hours":
      return 12 * 60;
    case "24Hours":
      return 24 * 60;
    case "3Days":
      return 3 * 24 * 60;
    case "7Days":
      return 7 * 24 * 60;
    case "All":
    case "OlderThan7Days":
    default:
      return null;
  }
};

export const buildDismissNotificationsPayload = (
  filters: NotificationFilterState,
  localCurrentTime: Date,
  selectedTab: NotificationStatusTab
): DismissNotificationsPayload => {
  const payload: DismissNotificationsPayload = {
    invalid: selectedTab === "inactive",
  };

  if (filters.category !== "All") {
    payload.category = filters.category.toUpperCase();
  }

  if (filters.severity !== "ALL") {
    payload.severity = filters.severity;
  }

  if (filters.time !== "All") {
    const windowInMinutes = getTimeFilterWindowInMinutes(filters.time);
    let generatedTo = format(localCurrentTime, DISMISS_PAYLOAD_DATE_FORMAT);

    if (filters.time === "OlderThan7Days") {
      payload.generatedFrom = OLDEST_NOTIFICATION_DATE;
      generatedTo = format(
        subMinutes(localCurrentTime, 7 * 24 * 60),
        DISMISS_PAYLOAD_DATE_FORMAT
      );
    } else {
      const generatedFromDate =
        windowInMinutes === null
          ? startOfDay(localCurrentTime)
          : subMinutes(localCurrentTime, windowInMinutes);
      payload.generatedFrom = format(
        generatedFromDate,
        DISMISS_PAYLOAD_DATE_FORMAT
      );
    }

    payload.generatedTo = generatedTo;
  }

  return payload;
};

export const matchesSelectedTimeFilter = (
  notificationTimestamp: string,
  selectedTimeFilter: TimeFilter,
  currentTime: Date
): boolean => {
  if (selectedTimeFilter === "All") {
    return true;
  }

  const notificationTime = new Date(notificationTimestamp);
  if (Number.isNaN(notificationTime.getTime())) {
    return false;
  }

  const currentTimeMs = currentTime.getTime();
  const notificationTimeMs = notificationTime.getTime();
  const differenceInMinutes =
    (currentTimeMs - notificationTimeMs) / (1000 * 60);

  if (differenceInMinutes < 0) {
    return false;
  }

  if (selectedTimeFilter === "OlderThan7Days") {
    return differenceInMinutes > 7 * 24 * 60;
  }

  const windowInMinutes = getTimeFilterWindowInMinutes(selectedTimeFilter);
  if (windowInMinutes === null) {
    return true;
  }

  return differenceInMinutes <= windowInMinutes;
};

export const filterAndSortNotificationsByStatus = (
  notifications: NotificationItem[],
  status: NotificationStatusTab
): NotificationItem[] => {
  const isInactive = status === "inactive";
  const getTimestamp = (value: string | null): number | null => {
    if (!value) {
      return null;
    }

    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  };

  return notifications
    .filter((notification) => notification.invalid === isInactive)
    .sort((a, b) => {
      const aDate = getTimestamp(isInactive ? a.updatedTimestamp : a.timestamp);
      const bDate = getTimestamp(isInactive ? b.updatedTimestamp : b.timestamp);

      if (aDate === null) {
        return bDate === null ? 0 : 1;
      }
      if (bDate === null) {
        return -1;
      }

      return bDate - aDate;
    });
};

export const formatFilteredNotifications = (
  notifications: NotificationItem[],
  isLocalTime: boolean,
  airportTimeZone: string,
  currentTime: Date,
  showTodayLabel = false
): NotificationItem[] => {
  const getDateToFormat = (value: string): Date =>
    isLocalTime
      ? new Date(value)
      : convertToUTC(fromZonedTime(value, airportTimeZone));

  const formatDateValue = (
    value: string | null | undefined,
    todayLabel = false
  ): string => {
    if (!value) {
      return "";
    }

    const date = getDateToFormat(value);
    return !date || Number.isNaN(date.getTime())
      ? ""
      : formatRelativeTime(date, currentTime, todayLabel);
  };

  return notifications.map((notification) => {
    return {
      ...notification,
      body: processMessageTitle(
        notification.body,
        airportTimeZone,
        isLocalTime
      ),
      timestamp: formatDateValue(notification.timestamp, showTodayLabel),
      updatedTimestamp: notification.updatedTimestamp
        ? formatDateValue(notification.updatedTimestamp, showTodayLabel)
        : null,
      start: formatDateValue(notification.start),
      end: formatDateValue(notification.end),
    };
  });
};

export function getSeverityColor(severity: string): BadgeColor {
  switch (severity.toUpperCase()) {
    case "CRITICAL":
    case "HIGH":
      return "danger";
    case "MEDIUM":
    case "LOW":
      return "warning";
    default:
      return "informative";
  }
}

export function getNotificationCategoryLabel(category: string): string {
  return (
    CATEGORY_OPTIONS.find((option) => option.value === category)?.label ??
    toSentenceCase(category)
  );
}

export function getNotificationCategoryIcon(category: string): string | null {
  switch (category.trim().toUpperCase()) {
    case "WEATHER":
      return weatherIcon;
    case "PAX_SECURITY":
      return paxIcon;
    case "BAGGAGE":
      return baggageIcon;
    default:
      return null;
  }
}
